import React, { useState, useEffect } from "react";
import { Paper, Typography, Grid2 } from "@mui/material";
import { useForm } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import BreadCrumbs from "@components/breadCrumbs/breadCrumbs";
import CustomButton from "@components/button/customButton";
import ConfirmDialog from "@components/dialog/confirmDialog";
import SkeletonWrapper from "@components/skeleton/skeletonWrapper";
import ControlledTextField from "@components/inputs/controlledTextField";
import ImagePicker from "@components/inputs/imagePicker";

import { useSnackbar } from "@context/snackbarContext";

import { useAuthentication } from "@redux/authentication/hook";

import { AdminApiEndpoints } from "@configuration/apiEndpoints";


const schema = z.object({
  taglineCntrad: z.string().min(1, { message: '標語 is required' }),
  headerCntrad: z.string().min(1, { message: '標頭 is required' }),
  presidentCntrad: z.string().min(1, { message: '理事長 is required' }),
  vicePresidentCntrad: z.string().min(1, { message: '副理事長 is required' }),
  secretaryCntrad: z.string().min(1, { message: '秘書長 is required' }),
  treasurerCntrad: z.string().min(1, { message: '財務長 is required' }),
  boardMemberCntrad: z.string().min(1, { message: '理事 is required' }),

  taglineCnsimp: z.string().min(1, { message: '标语 is required' }),
  headerCnsimp: z.string().min(1, { message: '标头 is required' }),
  presidentCnsimp: z.string().min(1, { message: '理事长 is required' }),
  vicePresidentCnsimp: z.string().min(1, { message: '副理事长 is required' }),
  secretaryCnsimp: z.string().min(1, { message: '秘书长 is required' }),
  treasurerCnsimp: z.string().min(1, { message: '财务长 is required' }),
  boardMemberCnsimp: z.string().min(1, { message: '理事 is required' }),

  taglineEng: z.string().min(1, { message: 'Tagline is required' }),
  headerEng: z.string().min(1, { message: 'Header is required' }),
  presidentEng: z.string().min(1, { message: 'President is required' }),
  vicePresidentEng: z.string().min(1, { message: 'Vice President is required' }),
  secretaryEng: z.string().min(1, { message: 'Secretary is required' }),
  treasurerEng: z.string().min(1, { message: 'Treasurer is required' }),
  boardMemberEng: z.string().min(1, { message: 'Board Members is required' }),
});

export default function Section4Form() {
  const openSnackbar = useSnackbar();
  const { user } = useAuthentication() 

  const { handleSubmit, control, getValues, reset } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
    },
  });

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  useEffect(()=>{
    fetchSectionMutation.mutate()
  },[])

  const fetchSectionMutation = useMutation({
    mutationFn: () => {
      return axios.get(`${AdminApiEndpoints.getAboutUsSection4}`)
    },
    onSuccess: (res) => {
      const data = res.data
      reset(data)
    },
    onError: (err) => {
      openSnackbar("Failed to retrieve section", "error");
    }
  });

  /*const createSectionMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${AdminApiEndpoints.createAboutUsSection4}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      openSnackbar("Successfully created section", "success");
      fetchSectionMutation.mutate()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to create section", "error");
    }
  })*/

  const updateSectionMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${AdminApiEndpoints.updateAboutUsSection4}`, {...data, by: user.username})
    },
    onSuccess: () => {
      setOpenConfirmDialog(false)
      openSnackbar("Successfully updated section", "success");
      fetchSectionMutation.mutate()
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      if (err?.response) openSnackbar(err?.response?.data, "error");
      else openSnackbar("Failed to update section", "error");
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues();
    updateSectionMutation.mutate(data)
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
      <BreadCrumbs />
      <SkeletonWrapper isLoading={fetchSectionMutation.isPending} isError={fetchSectionMutation.isError} errorMessage="Unable to load section">
      <form onSubmit={handleSubmit(onSubmit)} style={{display: 'flex', flexDirection: 'column', gap: "25px"}}>
        <Grid2 container spacing={"25px"}>
          
          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>繁体中文</Typography>
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="taglineCntrad"
                    control={control}
                    label="標語"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerCntrad"
                    control={control}
                    label="標頭"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="presidentCntrad"
                    control={control}
                    label="理事長"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="vicePresidentCntrad"
                    control={control}
                    label="副理事長"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="secretaryCntrad"
                    control={control}
                    label="秘書長"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="treasurerCntrad"
                    control={control}
                    label="財務長"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="boardMemberCntrad"
                    control={control}
                    label="理事"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>简体中文</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <ControlledTextField 
                    name="taglineCnsimp"
                    control={control}
                    label="标语"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerCnsimp"
                    control={control}
                    label="标头"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="presidentCnsimp"
                    control={control}
                    label="理事长"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="vicePresidentCnsimp"
                    control={control}
                    label="副理事长"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="secretaryCnsimp"
                    control={control}
                    label="秘书长"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="treasurerCnsimp"
                    control={control}
                    label="财务长"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="boardMemberCnsimp"
                    control={control}
                    label="理事"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

          <Grid2 size={{ lg: 6, md: 12, sm: 12, xs: 12 }}>
            <Paper
              sx={{
                p: "20px 25px",
                boxShadow: "none",
                borderRadius: "10px",
                border: "1px solid #E5E5E5",
              }}
            >
              <Grid2 container spacing={"20px"}>
                <Grid2 size={12}>
                  <Typography fontSize={"0.9rem"} color="#202020" fontWeight={600}>English</Typography>
                </Grid2>
                <Grid2 size={12}>
                  <ControlledTextField 
                    name="taglineEng"
                    control={control}
                    label="Tagline"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="headerEng"
                    control={control}
                    label="Header"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="presidentEng"
                    control={control}
                    label="President"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="vicePresidentEng"
                    control={control}
                    label="Vice President"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="secretaryEng"
                    control={control}
                    label="Secretary"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="treasurerEng"
                    control={control}
                    label="Treasurer"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

                <Grid2 size={12}>
                  <ControlledTextField 
                    name="boardMemberEng"
                    control={control}
                    label="Board Members"
                    disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}
                  />
                </Grid2>

              </Grid2>
            </Paper>
          </Grid2>

        </Grid2>

        <Paper
          sx={{
            p: "20px 25px",
            boxShadow: "none",
            borderRadius: "10px",
            border: "1px solid #E5E5E5",
            display: "flex",
            justifyContent: 'flex-end',
            gap: "10px"
          }}
        >
          <CustomButton type="submit" disabled={fetchSectionMutation.isPending || fetchSectionMutation.isError}>Save</CustomButton>
        </Paper>
        
      </form>
      </SkeletonWrapper>
      <ConfirmDialog
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        dialogTitle="Confirmation"
        dialogDescription="Update section?"
        onSubmit={()=>onConfirmSubmit()}
        loading={updateSectionMutation.isPending}
      />
    </div>
  );
}
